import React, { useEffect } from 'react';
import { UsersIcon, ClockIcon, AlertCircleIcon, TruckIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { isStringKey } from '../../data/i18n';
import { isWeekend, offerMayApply } from '../../data/offers';
import { hrs, usd } from '../../utils/format';
import { HelpAnswers } from './HelpAnswers';
const estCopy = {
  en: {
    mode: 'Pricing: hourly estimate',
    modeNote: 'Your final bill follows actual service time at the accepted rate, plus any changes you approve. This estimate is not a fixed or maximum price.',
    truck: 'Truck you requested',
    truckHelp: 'Help me choose',
    truckNone: 'No preference',
    truckSub: 'Your preference. Dispatch confirms the assigned truck.',
    services: 'Extra services',
    servicesValue: 'Time and any charge confirmed by our team before you book',
    stops: 'Extra stops',
    svc: {
      disassembly: 'Disassembly',
      reassembly: 'Reassembly',
      placement: 'Room placement',
      garageOnly: 'Garage-only delivery',
      unpacking: 'Unpacking'
    },
    offer: 'You may qualify for a reduced weekday rate for 2 movers. Our team confirms eligibility and issues the rate before you book. The estimate below uses the standard rate.',
    taxes: 'Taxes',
    taxesValue: 'None added. Any applicable tax is shown before payment.'
  },
  es: {
    mode: 'Precio: estimado por hora',
    modeNote: 'Tu factura final sigue el tiempo real de servicio a la tarifa aceptada, más los cambios que apruebes. Este estimado no es un precio fijo ni máximo.',
    truck: 'Camión que solicitó',
    truckHelp: 'Ayúdenme a elegir',
    truckNone: 'Sin preferencia',
    truckSub: 'Es su preferencia; despacho confirma el camión asignado.',
    services: 'Servicios adicionales',
    servicesValue: 'Tiempo y cualquier cargo confirmados por nuestro equipo antes de reservar',
    stops: 'Paradas adicionales',
    svc: {
      disassembly: 'Desarme',
      reassembly: 'Armado',
      placement: 'Colocación por habitación',
      garageOnly: 'Entrega en garaje',
      unpacking: 'Desempaque'
    },
    offer: 'Podrías calificar para una tarifa reducida entre semana con 2 trabajadores. Nuestro equipo confirma si aplica y emite la tarifa antes de reservar. El estimado usa la tarifa estándar.',
    taxes: 'Impuestos',
    taxesValue: 'No se agregan. Cualquier impuesto aplicable se muestra antes de pagar.'
  }
};
export function StepEstimate() {
  const {
    t,
    lang,
    quote,
    data,
    track
  } = useFunnel();
  const crew = quote.crew;
  const x = estCopy[lang];
  useEffect(() => {
    track('EstimatePresented', {
      quote: quote.hash
    });
  }, [track, quote.hash]);
  const truck = data.truckPreference ? data.truckPreference === 'help' ? x.truckHelp : `${data.truckPreference} ft` : x.truckNone;
  const svc = [data.needsDisassembly && x.svc.disassembly, data.services.reassembly && x.svc.reassembly, data.services.placement && x.svc.placement, data.services.garageOnly && x.svc.garageOnly, data.services.unpacking && x.svc.unpacking].filter(Boolean) as string[];
  const offer = offerMayApply(data, crew.used) && !isWeekend(data.moveDate);
  return <div data-id="element-716" data-mp="lulqg7lxcat1">
      <div className="mb-7" data-id="element-717" data-mp="luu07qmr3000">
        <h1 className="font-display text-3xl font-black leading-tight text-magenta sm:text-4xl" data-id="element-718" data-mp="lxpy5eylloog">{t('estTitle')}</h1>
        <p className="mt-2 max-w-2xl text-base font-semibold leading-relaxed text-ink" data-id="element-719" data-mp="uwbh3b7zf5pr">{t('priceClear')}</p>
        <p className="mt-1 max-w-2xl text-base leading-relaxed text-ink-600" data-id="element-720" data-mp="75xw24nte74o">{t('estSub')}</p>
      </div>

      <section className="rounded-2xl bg-ink text-white" data-id="element-721" data-mp="285u0htgh4mr">
        <div className="grid gap-6 p-6 sm:grid-cols-2" data-id="element-722" data-mp="25jn8kee2t5l">
          <div data-id="element-723" data-mp="gxc08pqpnv45">
            <p className="flex items-center gap-2 text-sm font-medium text-ink-300" data-id="element-724" data-mp="gxv97gydv961">
              <UsersIcon className="h-4 w-4" aria-hidden="true" data-id="element-725" data-mp="c4se34dyjrnq" />
              {t('crewLabel')} · {crew.used === crew.recommended ? t('recommended') : t('yourChoice')}
            </p>
            <p className="mt-2 font-display text-4xl font-black" data-id="element-726" data-mp="3fn2eeds16ga7">{t('moversN', {
              n: crew.used
            })}</p>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-200" data-id="element-727" data-mp="2ivypa79um6l8">{isStringKey(crew.reasonKey) ? t(crew.reasonKey) : ''}</p>
          </div>
          <div data-id="element-728" data-mp="2nrw7yk8l3p16">
            <p className="flex items-center gap-2 text-sm font-medium text-ink-300" data-id="element-729" data-mp="2rvlu731sduj1">
              <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-730" data-mp="2rtr6jgz4rob9" />
              {t('serviceLabel')}
            </p>
            <p className="mt-2 font-display text-4xl font-black text-lime" data-id="element-731" data-mp="31ldmfqmeybkc">{t('hoursRange', {
              a: hrs(quote.serviceLow),
              b: hrs(quote.serviceHigh)
            })}</p>
            <p className="mt-3 text-sm text-ink-300" data-id="element-732" data-mp="36ix1utuukudv">{t('minimumNote')}</p>
            <p className="mt-4 flex items-center gap-2 text-sm text-ink-200" data-id="element-733" data-mp="3bjb9iasje2bd">
              <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-734" data-mp="3fkwny0kpyono" />
              {x.truck}: <span className="font-semibold text-white" data-id="element-735" data-mp="3fitwdc3x4ngu">{truck}</span>
            </p>
          </div>
        </div>
      </section>

      {offer && <p className="mt-4 rounded-xl bg-lime-soft p-4 text-sm font-medium text-ink" data-id="element-736" data-mp="1l6dknvscsh4l">{x.offer}</p>}

      {quote.reviewReasons.length > 0 && <section className="mt-6 rounded-xl border border-ink-300 bg-white p-5" data-id="element-737" data-mp="1dppd4rk5ayda">
          <h2 className="flex items-center gap-2 font-semibold text-ink" data-id="element-738" data-mp="18vctagerbl10">
            <AlertCircleIcon className="h-5 w-5 text-magenta" aria-hidden="true" data-id="element-739" data-mp="13yuqhm2zlwrb" />
            {t('reviewTitle')}
          </h2>
          <ul className="mt-3 space-y-1.5 pl-7 text-[15px] text-ink-700" data-id="element-740" data-mp="zxsm6qu7quih">
            {quote.reviewReasons.map(r => <li key={r} className="list-disc" data-id="element-741" data-mp="1wk4euoxud54t">
                {isStringKey(r) ? t(r) : r}
              </li>)}
          </ul>
        </section>}

      <section className="mt-8" data-id="element-742" data-mp="1nkhe85cxs3ct">
        <p className="mb-2 text-sm font-semibold text-ink" data-id="element-743" data-mp="1nnn25h9twz6y">{x.mode}</p>
        <dl className="divide-y divide-ink-200 rounded-2xl bg-white px-5 text-[15px] ring-1 ring-ink-200" data-id="element-744" data-mp="1im7cfbwpi4i8">
          <Row label={t('lineRate')} sub={t('rateLabel', {
          n: crew.used
        })} value={quote.hourlyRate !== null ? `${usd(quote.hourlyRate, lang)}/h` : t('rateByTeam')} data-id="element-745" data-mp="c7yf6pwks065" />
          <Row label={t('lineService')} value={t('hoursRange', {
          a: hrs(quote.serviceLow),
          b: hrs(quote.serviceHigh)
        })} data-id="element-746" data-mp="8pr4nce2qbij" />
          <Row label={t('lineTravel')} value={t('hoursN', {
          n: quote.travelHours
        })} data-id="element-747" data-mp="dnak2fmicuc2" />
          <Row label={t('lineLabor')} value={quote.laborLow !== null && quote.laborHigh !== null ? `${usd(quote.laborLow, lang)} – ${usd(quote.laborHigh, lang)}` : '—'} strong data-id="element-748" data-mp="3vsnju18gs1oi" />
          <Row label={t('lineSurcharge')} value={quote.surchargeBaseApproved ? '—' : t('surchargePending')} muted data-id="element-749" data-mp="3m3cb4fphoz15" />
          {quote.packingSelected && <Row label={t('linePacking')} value={t('pricedByTeam')} muted data-id="element-750" data-mp="3qvnddp7pcwwv" />}
          {svc.length > 0 && <Row label={x.services} sub={svc.join(' · ')} value={x.servicesValue} muted data-id="element-751" data-mp="3c79cvkxrdkjh" />}
          {data.stops.length > 0 && <Row label={x.stops} value={String(data.stops.length)} sub={t('rr_stops')} data-id="element-752" data-mp="3h2g6u1187av7" />}
          {quote.needsTransport && <Row label={t('lineTransport')} value={t('transportValue')} sub={t('rr_transport')} muted data-id="element-753" data-mp="383uuinmcks2v" />}
          {quote.cardFeeLow !== null && quote.cardFeeHigh !== null && <Row label={t('lineCardFee', {
          p: quote.cardFeePercent
        })} sub={t('cardFeeNote')} value={`${usd(quote.cardFeeLow, lang)} – ${usd(quote.cardFeeHigh, lang)}`} data-id="element-754" data-mp="2ok5eti1j7vrn" />}
          <Row label={x.taxes} value={x.taxesValue} muted data-id="element-755" data-mp="2xkandvigp07e" />
          <Row label={t('lineTotal')} value={quote.surchargeBaseApproved ? '—' : t('totalPending')} sub={quote.laborLow !== null && quote.laborHigh !== null ? t('totalRange', {
          a: usd(quote.laborLow + (quote.cardFeeLow ?? 0), lang),
          b: usd(quote.laborHigh + (quote.cardFeeHigh ?? 0), lang)
        }) : undefined} strong data-id="element-756" data-mp="2tgmd352g3qig" />
          <Row label={t('lineDeposit')} value={usd(quote.deposit, lang)} sub={t('depositCredit')} strong data-id="element-757" data-mp="3c8yhrygkn3vr" />
          <Row label={t('lineRemaining')} value={quote.remainingLow !== null && quote.remainingHigh !== null ? `${usd(quote.remainingLow, lang)} – ${usd(quote.remainingHigh, lang)}` : '—'} sub={t('remainingNote')} data-id="element-758" data-mp="3q7y0yqlbeqkj" />
        </dl>
        <p className="mt-3 text-sm text-ink-600" data-id="element-759" data-mp="11deumeutvaiw">{x.modeNote}</p>
        <p className="mt-3 font-mono text-xs text-ink-400" data-id="element-760" data-mp="15g1pkomspbkb">
          {quote.configVersion} · #{quote.hash}
        </p>
        <p className="mt-4 text-sm text-ink-600" data-id="element-761" data-mp="1afo1muqw0lrj">{t('acceptNote')}</p>
      </section>

      <HelpAnswers screen="stepEstimate" className="mt-10" data-id="element-762" data-mp="dp5ffvs4dl0q" />
    </div>;
}
function Row({
  label,
  value,
  sub,
  strong,
  muted
}: {
  label: string;
  value: string;
  sub?: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 py-3.5" data-id="element-763" data-mp="moiq2rrzcfn1">
      <dt className={strong ? 'font-semibold text-ink' : 'text-ink-700'} data-id="element-764" data-mp="rg0g4ksgsvt8">{label}</dt>
      <dd className={`text-right ${strong ? 'font-bold text-ink' : muted ? 'max-w-[240px] text-sm text-ink-500' : 'font-semibold text-ink'}`} data-id="element-765" data-mp="3q6abtofiw3cs">{value}</dd>
      {sub && <p className="col-span-2 text-sm text-ink-500" data-id="element-766" data-mp="3v3smbj96uqmw">{sub}</p>}
    </div>;
}